import { Fade, Link, Stack, styled, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import PageHeadActions from "./PageHeadActions/page-head-actions";
import PageHeadLinkTabs from "./PageHeadLinkTabs/page-head-link-tabs";
import PageHeadBreadcrumbs from "./PageHeadBreadcrumb/page-head-breadcrumb";
import { NavTypes, PageHeadRouteProps, getRoutes } from "../../../Router/router";
import { matchRoutes, RouteObject, useLocation } from "react-router";
import { ReactElement, useEffect, useRef, useState } from "react";
import { cacheService } from "../../../Core/Api/services";
import { t } from "i18next";

interface IPageHeadProps {}

export type TPageHeadRoute = RouteObject & {
  element: ReactElement<PageHeadRouteProps>;
};

function isPageHeadRoute(route: RouteObject) {
  if (typeof route.element in ["string", "number", "boolean"]) return false;
  return (
    (route as RouteObject).element?.hasOwnProperty("props") &&
    //@ts-expect-error
    (route as RouteObject).element?.props.hasOwnProperty("pageTitle")
  );
}

const PageHead: React.FC<IPageHeadProps> = () => {
  const user = cacheService.GetUserInfo();
  const location = useLocation();
  const match = matchRoutes(getRoutes(), location);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);

  // Find component with page head infos
  const rootMatch = match?.find((r) => isPageHeadRoute(r.route));
  const route = rootMatch?.route as TPageHeadRoute;

  //  Overflowing nav behavior
  const navRef = useRef<HTMLDivElement>(null);
  const [navOverflowing, setNavOverflowing] = useState<boolean>(false);
  const [navExpanded, setNavExpanded] = useState<boolean>(false);
  const [scrollWidth, setScrollWidth] = useState<number>(0);

  useEffect(() => {
    const checkOverflow = () => {
      const el = navRef.current;
      if (el) {
        // For horizontal overflow (optional):
        const isHorizontallyOverflowing = el.scrollWidth > el.clientWidth;
        setScrollWidth(el.scrollWidth);
        setNavOverflowing(isHorizontallyOverflowing);
      }
    };

    // Check overflow when the component mounts
    checkOverflow();

    // Optional: Re-check on window resize
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, []);

  return (
    <PageHeadStyled>
      {!user?.isExternalAccessSpecific ? (
        <NavContainerStyled
          direction="row"
          alignItems={"center"}
          scrollWidth={scrollWidth}
          navExpanded={navExpanded}
          navOverflowing={navOverflowing}
          sx={{ flexShrink: 1 }}
          ref={navRef}
          onMouseEnter={() => setNavExpanded(true)}
          onMouseLeave={() => setNavExpanded(false)}
          className={navExpanded ? "expanded" : ""}
        >
          {route !== undefined && (
            <Fade in={!searchOpen}>
              <div>
                {route.element.props.navType === NavTypes.LinkTabs && <PageHeadLinkTabs route={route} />}
                {route.element.props.navType === NavTypes.Breadcrumb && <PageHeadBreadcrumbs route={route} />}
                {route.element.props.navType === NavTypes.None && (
                  <Link
                    variant="subtitle1"
                    to={route.element.props.rootLink}
                    component={RouterLink}
                    sx={{ color: "text.primary", fontWeight: 500, textDecoration: "none" }}
                  >
                    {route.element.props.pageTitle}
                  </Link>
                )}
              </div>
            </Fade>
          )}
        </NavContainerStyled>
      ) : (
        <Typography variant="subtitle1" fontWeight={600} sx={{ display: !searchOpen ? "block" : "none" }}>
          {t("page:body.welcomeUser", { user: user.firstName })}
        </Typography>
      )}
      <PageHeadActions searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
    </PageHeadStyled>
  );
};

export default PageHead;

const PageHeadStyled = styled(Stack, { shouldForwardProp: (prop) => prop !== "variant" })<{
  variant?: string;
}>(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  height: GlobalConst.HEADER_HEIGHT,
  boxSizing: "content-box",
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingBottom: "0",
  position: "relative",
  gap: theme.spacing(2),
  zIndex: theme.zIndex.drawer + 1,
}));

const NavContainerStyled = styled(Stack, {
  shouldForwardProp: (prop) =>
    prop !== "variant" && prop !== "navOverflowing" && prop !== "scrollWidth" && prop !== "navExpanded",
})<{
  navOverflowing: boolean;
  navExpanded: boolean;
  scrollWidth: number;
}>(({ theme, navExpanded, navOverflowing, scrollWidth }) => ({
  overflowX: "hidden",
  position: "relative",
  minWidth: navExpanded ? scrollWidth : 100,
  height: GlobalConst.HEADER_HEIGHT,
  pt: 2,
  pb: 2,
  paddingRight: navOverflowing ? 8 : 0,
  left: 0,
  // boxShadow: theme.helpers(theme).listPageHeaderShadow.container,
  transition: theme.transitions.create("min-width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));
